/**
 * Inline option (size) switching on a cart line.
 *
 * Shopify has no "change this line's variant" call, so a swap is an add of the
 * new variant plus a removal of the old one. The order matters and is the whole
 * point of this file:
 *
 *   1. add the new variant — if this fails (sold out, not enough stock, a
 *      variant that disappeared since the page rendered) nothing has been
 *      removed and the shopper still has exactly what they had;
 *   2. only then remove the old line, addressed by its line **key**, so a
 *      shifting line index cannot remove somebody else's product.
 *
 * A failure at step 2 leaves a duplicate, which is visible and recoverable. The
 * reverse order would risk an empty cart, which is not.
 *
 * Both calls go through window.Cart, so they queue behind any other cart work
 * in flight and the drawer, the header badge and the reward bar all re-render
 * from one source. The add runs without section HTML so the drawer repaints
 * once, at the end, rather than flashing an intermediate state.
 *
 * THE PICKER
 * ----------
 * A `popover` listbox rather than a `<select>`, because the open list of a
 * select is the browser's own popup and takes no styling — row height,
 * padding, radius and hover colour are all the platform's. `popover` keeps the
 * two things the select was actually doing for us: the top layer, so the list
 * is not clipped by the scrolling drawer body, and light dismiss plus Escape.
 * What it does not keep is the keyboard, which is the arrow/Home/End handling
 * below. Type-to-jump is the one select behaviour deliberately not reproduced:
 * with S, M, L and 2X there is nothing to disambiguate by first letter.
 *
 * @extends HTMLElement
 */
export class CartLineItem extends HTMLElement {
  constructor() {
    super();
    this.onListToggle = this.onListToggle.bind(this);
    this.onOptionClick = this.onOptionClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.position = this.position.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-option-trigger]');
    this.list = this.querySelector('[data-option-list]');
    if (!this.trigger || !this.list) return;

    this.list.addEventListener('toggle', this.onListToggle);
    this.list.addEventListener('click', this.onOptionClick);
    this.list.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    this.list?.removeEventListener('toggle', this.onListToggle);
    this.list?.removeEventListener('click', this.onOptionClick);
    this.list?.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('resize', this.position);
    this.scrollParent?.removeEventListener('scroll', this.position);
  }

  get errorElement() {
    return this.closest('[data-cart-line]')?.querySelector('[data-cart-line-error]');
  }

  /** @returns {HTMLElement[]} Every option, sold-out ones included. */
  get options() {
    return Array.from(this.list?.querySelectorAll('[role="option"]') || []);
  }

  /** @returns {HTMLElement[]} Only the options that can be picked. */
  get selectableOptions() {
    return this.options.filter((option) => option.getAttribute('aria-disabled') !== 'true');
  }

  get selectedOption() {
    return this.options.find((option) => option.getAttribute('aria-selected') === 'true') || null;
  }

  /**
   * Line item properties as rendered by Liquid, including the private `_`-
   * prefixed ones, so a swap does not silently drop personalisation.
   *
   * `item.properties | json` serialises as `[key, value]` pairs, not an object.
   * Read as an object, those pairs re-added the line with properties named
   * "0", "1", "2" holding "Name,Smith" — and exposed `_team_identity`.
   * @returns {Object}
   */
  get lineProperties() {
    try {
      const properties = JSON.parse(this.dataset.lineProperties || '{}') || {};
      return Array.isArray(properties) ? Object.fromEntries(properties) : properties;
    } catch {
      return {};
    }
  }

  /* ----- the list ----- */

  /**
   * Places the list under the trigger, or above it when there is no room.
   *
   * Written from JavaScript because the list is in the top layer and CSS
   * anchor positioning is not baseline yet. Re-run on scroll and resize: the
   * drawer body scrolls under an open popover, and a list pinned to stale
   * coordinates would drift away from its trigger.
   *
   * The list stays `visibility: hidden` until this has run once. `toggle` is
   * dispatched a task after the popover opens, so without that a frame could
   * paint the list at the viewport's top-left before it jumped into place.
   */
  position() {
    if (!this.list?.matches(':popover-open')) return;

    const anchor = this.trigger.getBoundingClientRect();
    /* Read the width before placing, so the list can be at least as wide as
       the pill it belongs to without ever being narrower than its content. */
    this.list.style.setProperty('--cart-option-list-width', `${Math.round(anchor.width)}px`);

    const list = this.list.getBoundingClientRect();
    const gap = 4;
    const margin = 8;

    const spaceBelow = window.innerHeight - anchor.bottom;
    const openUp = spaceBelow < list.height + gap + margin && anchor.top > spaceBelow;

    const top = openUp ? anchor.top - list.height - gap : anchor.bottom + gap;
    const left = Math.min(Math.max(anchor.left, margin), window.innerWidth - list.width - margin);

    this.list.style.top = `${Math.round(Math.max(top, margin))}px`;
    this.list.style.left = `${Math.round(left)}px`;
    this.list.dataset.positioned = '';
  }

  onListToggle(event) {
    const open = event.newState === 'open';
    this.trigger.setAttribute('aria-expanded', String(open));

    if (!open) {

      delete this.list.dataset.positioned;
      window.removeEventListener('resize', this.position);
      this.scrollParent?.removeEventListener('scroll', this.position);
      return;
    }

    this.position();

    /* Focus the current size, so the arrow keys start from where the shopper
       actually is rather than from the top of the list. */
    const start = this.selectedOption || this.selectableOptions[0];
    start?.focus();
    start?.scrollIntoView({ block: 'nearest' });

    this.scrollParent = this.closest('.cart-drawer__body');
    window.addEventListener('resize', this.position);
    this.scrollParent?.addEventListener('scroll', this.position);
  }

  onOptionClick(event) {
    const option = event.target.closest('[role="option"]');
    if (!option) return;

    /* A sold-out size stays in the list because "no 3X" is worth knowing, so
       the click has to be refused here rather than by a disabled attribute. */
    if (option.getAttribute('aria-disabled') === 'true') return;

    this.choose(option);
  }

  onKeyDown(event) {
    const options = this.selectableOptions;
    if (options.length === 0) return;

    const current = options.indexOf(document.activeElement);
    let next = -1;

    switch (event.key) {
      case 'ArrowDown':
        next = current < 0 ? 0 : Math.min(current + 1, options.length - 1);
        break;
      case 'ArrowUp':
        next = current < 0 ? options.length - 1 : Math.max(current - 1, 0);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = options.length - 1;
        break;
      case 'Enter':
      case ' ':
        if (current >= 0) {
          event.preventDefault();
          this.choose(options[current]);
        }
        return;
      default:
        return;
    }

    /* Only after a key we handled: Tab, Escape and typing must keep their own
       behaviour, and Escape closing the popover is the browser's. */
    event.preventDefault();
    options[next].focus();
    options[next].scrollIntoView({ block: 'nearest' });
  }

  /**
   * Commits a pick: closes the list, moves the selection, then swaps the line.
   * @param {HTMLElement} option
   */
  choose(option) {
    const variantId = option.dataset.optionValue;
    this.list.hidePopover();

    /* Read before select() moves it: this is what the revert path restores if
       the add fails, and reading it afterwards would "restore" the size the
       shopper just failed to switch to. */
    const previous = this.selectedOption;
    if (option === previous) return;

    this.select(option);
    this.swap(variantId, previous);
  }

  /**
   * Moves `aria-selected` and updates the closed trigger's label.
   * @param {HTMLElement} option
   */
  select(option) {
    for (const other of this.options) {
      other.setAttribute('aria-selected', String(other === option));
    }

    const label = this.trigger.querySelector('[data-option-trigger-label]');
    const text = option.querySelector('.cart-drawer__item-option-label')?.textContent.trim();
    if (label && text) label.textContent = text;
  }

  setBusy(busy) {
    this.trigger.disabled = busy;
    this.toggleAttribute('data-busy', busy);
  }

  showError(message) {
    const target = this.errorElement;
    if (target) target.textContent = message;
  }

  clearError() {
    const target = this.errorElement;
    if (target) target.textContent = '';
  }

  buildFormData(variantId, quantity) {
    const form = new FormData();
    form.append('id', variantId);
    form.append('quantity', quantity);

    const properties = this.lineProperties;
    for (const [key, value] of Object.entries(properties)) {
      if (value !== null && value !== undefined) form.append(`properties[${key}]`, value);
    }

    const sellingPlan = this.dataset.lineSellingPlan;
    if (sellingPlan) form.append('selling_plan', sellingPlan);

    return form;
  }

  /**
   * Add the new variant, then remove the old line. See the file header for why
   * that order is not negotiable.
   * @param {string} variantId
   * @param {HTMLElement|null} previous The option to put back if the add fails
   */
  async swap(variantId, previous) {
    const cart = window.Cart;
    const lineKey = this.dataset.lineKey;
    const quantity = parseInt(this.dataset.lineQuantity, 10) || 1;

    if (!cart || !variantId || !lineKey) return;

    this.clearError();
    this.setBusy(true);

    try {
      await cart.add(this.buildFormData(variantId, quantity), this, { withSections: false });
    } catch (error) {
      /* Nothing was removed. Put the control back where the shopper found it. */
      if (previous) this.select(previous);
      this.showError(error.description || this.dataset.errorFailed);
      this.setBusy(false);
      return;
    }

    try {
      await cart.change({ id: lineKey, quantity: 0 }, this);
    } catch {
      /* The new line is in the cart and the old one is too. Say so plainly and
         re-sync, rather than leaving the shopper to discover it at checkout. */
      this.showError(this.dataset.errorPartial);
      this.setBusy(false);
      cart.refresh();
      return;
    }

    /* On success the drawer has already re-rendered this element away. */
    this.setBusy(false);
  }
}

if (!customElements.get('cart-line-item')) {
  customElements.define('cart-line-item', CartLineItem);
}
